import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Bath, Maximize, Sofa, MapPin, Heart, Share2, Printer, ChevronLeft, ChevronRight, X, Check, Home, FileText, Hash } from "lucide-react";
import api from "@/lib/api";
import EnquiryForm from "@/components/EnquiryForm";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { priceLabel, statusColor, formatDate } from "@/lib/format";
import { useSaved } from "@/context/SavedContext";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const { isSaved, toggleSave } = useSaved();

  useEffect(() => {
    setP(null); setNotFound(false); setActive(0);
    api.get(`/properties/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-5 py-24 text-center">
      <h1 className="font-display text-3xl font-bold">Property not found</h1>
      <p className="mt-3 text-muted-foreground">This property may no longer be available.</p>
      <Link to="/properties"><Button className="mt-6 rounded-full bg-[#C81D35] hover:bg-[#a5162b]">Browse properties</Button></Link>
    </div>
  );

  if (!p) return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 animate-pulse">
      <div className="h-8 w-2/3 bg-muted rounded mb-6" />
      <div className="aspect-[16/9] bg-muted rounded-2xl" />
    </div>
  );

  const images = p.images?.length ? p.images : [];
  const location = [p.area, p.town, p.county].filter(Boolean).join(", ");
  const saved = isSaved(p.id);

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: p.title, url: window.location.href });
      else { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied to clipboard"); }
    } catch {}
  };

  const detailRows = [
    ["Property type", p.property_type], ["Bedrooms", p.bedrooms], ["Bathrooms", p.bathrooms],
    ["Reception rooms", p.reception_rooms], ["Size", p.size ? `${p.size} ${p.size_unit || "sq ft"}` : null],
    ["Tenure", p.tenure], ["Council tax band", p.council_tax_band], ["EPC rating", p.epc_rating],
    ["Furnished", p.furnished_status], ["Available", p.availability_date], ["Year built", p.year_built],
    ["Parking", p.parking_spaces], ["Reference", p.reference],
  ].filter(([, v]) => v != null && v !== "");

  return (
    <div className="pb-16">
      {/* Breadcrumb + gallery */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6">
        <nav className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#C81D35]">Home</Link><span>/</span>
          <Link to="/properties" className="hover:text-[#C81D35]">Properties</Link><span>/</span>
          <span className="text-[#111827] font-medium truncate">{p.title}</span>
        </nav>
      </div>

      {images.length > 0 && (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-3 h-[300px] sm:h-[460px]">
            <button onClick={() => setLightbox(true)} className="relative rounded-2xl overflow-hidden group" data-testid="gallery-main">
              <img src={images[active].url} alt={images[active].alt || p.title} className="w-full h-full object-cover" />
              <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full">{active + 1} / {images.length}</span>
            </button>
            <div className="hidden lg:grid grid-rows-2 gap-3">
              {images.slice(1, 3).map((img, i) => (
                <button key={i} onClick={() => { setActive(i + 1); setLightbox(true); }} className="relative rounded-2xl overflow-hidden">
                  <img src={img.url} alt={img.alt || p.title} className="w-full h-full object-cover" />
                  {i === 1 && images.length > 3 && <span className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold">+{images.length - 3} more</span>}
                </button>
              ))}
            </div>
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-20 h-16 rounded-lg overflow-hidden shrink-0 border-2 ${active === i ? "border-[#C81D35]" : "border-transparent"}`}>
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-8 grid lg:grid-cols-[1fr_400px] gap-10">
        <div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {p.status && <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${statusColor(p.status)}`}>{p.status}</span>}
                {p.is_new && <span className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase bg-[#C5A059] text-white">New</span>}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#111827] tracking-tight">{p.title}</h1>
              {location && <p className="mt-2 text-muted-foreground flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {location} {p.postcode ? `· ${p.postcode}` : ""}</p>}
            </div>
            <p className="font-display text-3xl font-bold text-[#C81D35]">{priceLabel(p) || "POA"}</p>
          </div>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-5 text-[#111827] bg-white border border-border rounded-xl px-5 py-3">
              {p.bedrooms != null && <span className="flex items-center gap-2"><BedDouble className="w-5 h-5 text-muted-foreground" /> {p.bedrooms} bed</span>}
              {p.bathrooms != null && <span className="flex items-center gap-2"><Bath className="w-5 h-5 text-muted-foreground" /> {p.bathrooms} bath</span>}
              {p.size && <span className="flex items-center gap-2"><Maximize className="w-5 h-5 text-muted-foreground" /> {p.size} {p.size_unit || "sq ft"}</span>}
              {p.furnished_status && <span className="flex items-center gap-2"><Sofa className="w-5 h-5 text-muted-foreground" /> {p.furnished_status}</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" className="rounded-full" onClick={() => toggleSave(p)} data-testid="detail-save"><Heart className={`w-4 h-4 ${saved ? "fill-[#C81D35] text-[#C81D35]" : ""}`} /></Button>
              <Button variant="outline" size="icon" className="rounded-full" onClick={share} data-testid="detail-share"><Share2 className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" className="rounded-full" onClick={() => window.print()} data-testid="detail-print"><Printer className="w-4 h-4" /></Button>
            </div>
          </div>

          {p.summary && <p className="mt-6 text-lg text-[#111827]/80 leading-relaxed">{p.summary}</p>}

          {p.description && (
            <section className="mt-8">
              <h2 className="font-display text-2xl font-bold text-[#111827]">Description</h2>
              <div className="mt-3 prose prose-sm max-w-none text-[#111827]/80" dangerouslySetInnerHTML={{ __html: p.description }} />
            </section>
          )}

          {detailRows.length > 0 && (
            <section className="mt-8">
              <h2 className="font-display text-2xl font-bold text-[#111827]">Property details</h2>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                {detailRows.map(([k, v]) => (
                  <div key={k} className="flex justify-between py-2.5 border-b border-border">
                    <dt className="text-muted-foreground">{k}</dt><dd className="font-semibold text-[#111827]">{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {p.features?.length > 0 && (
            <section className="mt-8">
              <h2 className="font-display text-2xl font-bold text-[#111827]">Key features</h2>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-[#111827]/80">
                    <span className="w-5 h-5 rounded-full bg-[#C81D35]/10 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-[#C81D35]" /></span>{f}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Location (no map key -> written location) */}
          {location && (
            <section className="mt-8">
              <h2 className="font-display text-2xl font-bold text-[#111827]">Location</h2>
              <div className="mt-4 rounded-2xl border border-border bg-muted/50 p-6 flex items-center gap-3">
                <MapPin className="w-6 h-6 text-[#C81D35]" />
                <div>
                  <p className="font-semibold text-[#111827]">{location}</p>
                  {p.postcode && <p className="text-sm text-muted-foreground">{p.hide_exact_address ? "Approximate area" : p.postcode}</p>}
                </div>
              </div>
              {(p.nearby_transport || p.nearby_schools || p.nearby_amenities) && (
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {p.nearby_transport && <NearbyCard label="Transport" text={p.nearby_transport} />}
                  {p.nearby_schools && <NearbyCard label="Schools" text={p.nearby_schools} />}
                  {p.nearby_amenities && <NearbyCard label="Amenities" text={p.nearby_amenities} />}
                </div>
              )}
            </section>
          )}

          <p className="mt-8 text-xs text-muted-foreground">
            Listed {formatDate(p.published_at || p.created_at)}. Details are supplied by the property partner and should be independently verified.
          </p>
        </div>

        {/* Enquiry sidebar */}
        <aside>
          <div className="sticky top-24 space-y-4">
            <div className="bg-white rounded-2xl border border-border shadow-sm p-6">
              <h2 className="font-display text-xl font-bold text-[#111827]">Enquire about this property</h2>
              <p className="text-sm text-muted-foreground mt-1">No obligation. The relevant agency will be in touch.</p>
              <div className="mt-5"><EnquiryForm property={p} /></div>
            </div>
            {p.agency && (
              <div className="bg-white rounded-2xl border border-border p-6" data-testid="agency-card">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Marketed by</p>
                <div className="mt-3 flex items-center gap-3">
                  {p.agency.logo ? <img src={p.agency.logo} alt={p.agency.name} className="w-12 h-12 rounded-lg object-contain" /> : <div className="w-12 h-12 rounded-lg bg-[#111827] text-white flex items-center justify-center font-bold">{p.agency.name?.[0]}</div>}
                  <div><p className="font-semibold text-[#111827]">{p.agency.name}</p>{p.agency.phone && <p className="text-sm text-muted-foreground">{p.agency.phone}</p>}</div>
                </div>
                {p.agency.slug && <Link to={`/agencies/${p.agency.slug}`} className="mt-3 inline-block text-sm text-[#C81D35] font-semibold">View agency →</Link>}
              </div>
            )}
          </div>
        </aside>
      </div>

      {p.similar?.length > 0 && (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-16">
          <h2 className="font-display text-2xl font-bold text-[#111827]">Similar properties</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.similar.map((s) => <PropertyCard key={s.id} property={s} />)}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center" data-testid="lightbox" onClick={() => setLightbox(false)}>
          <button className="absolute top-5 right-5 text-white" onClick={() => setLightbox(false)}><X className="w-8 h-8" /></button>
          <button className="absolute left-5 text-white p-2" onClick={(e) => { e.stopPropagation(); setActive((a) => (a - 1 + images.length) % images.length); }}><ChevronLeft className="w-10 h-10" /></button>
          <img src={images[active]?.url} alt="" className="max-h-[85vh] max-w-[85vw] object-contain" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-5 text-white p-2" onClick={(e) => { e.stopPropagation(); setActive((a) => (a + 1) % images.length); }}><ChevronRight className="w-10 h-10" /></button>
        </div>
      )}
    </div>
  );
}

function NearbyCard({ label, text }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <p className="text-xs font-semibold text-[#C81D35] uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-sm text-[#111827]/80">{text}</p>
    </div>
  );
}
